import { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard, SuperAdminGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

const Organizations = lazy(() => import('src/pages/superadmin/organizations'));
const OrganizationsCreate = lazy(() => import('src/pages/superadmin/organizations-create'));
const OrganizationsEdit = lazy(() => import('src/sections/superadmin/organizations-edit'));

const AdminUsers = lazy(() => import('src/pages/superadmin/admin-users'));
const AdminUsersCreate = lazy(() => import('src/pages/superadmin/admin-users-create'));
const AdminUsersEdit = lazy(() => import('src/sections/superadmin/admin-user-edit'));
const AdminManagePhoneNumbersPage = lazy(() =>
  import('src/pages/superadmin/admin-manage-phone-numbers')
);

const SystemStatus = lazy(() => import('src/pages/superadmin/system-status'));
const Announcements = lazy(() => import('src/pages/superadmin/announcements'));

const Users = lazy(() => import('src/pages/superadmin/users'));
const UsersCreate = lazy(() => import('src/pages/superadmin/users-create'));

// Same screen a manager gets at /users; it scopes itself to :orgId from the path.
const OrgUsers = lazy(() => import('src/pages/users/user-management'));
const OrgAddUser = lazy(() => import('src/sections/superadmin/organization-users-create'));
const OrgEditUser = lazy(() => import('src/sections/superadmin/organization-users-edit'));
const UserCreatePage = lazy(() => import('src/pages/users/user-create'));

const Campaigns = lazy(() => import('src/sections/superadmin/organization-campaigns'));
const CampaignsCreate = lazy(() => import('src/sections/superadmin/organization-campaigns-create'));
const CampaignsEdit = lazy(() => import('src/sections/superadmin/organization-campaigns-edit'));
const OrgTasks = lazy(() => import('src/sections/superadmin/organization-tasks'));
const OrgTasksCreate = lazy(() => import('src/sections/superadmin/organization-tasks-create'));
const OrgTasksEdit = lazy(() => import('src/sections/superadmin/organization-tasks-edit'));

const Planning = lazy(() => import('src/sections/planning/planning'));
const GeographicalSummary = lazy(() => import('src/sections/planning/geographical-summary'));
const NewVoterRegistrationSummary = lazy(() =>
  import('src/sections/planning/new-voter-registration-summary')
);
const PortentialVotersRemaining = lazy(() =>
  import('src/sections/planning/potential-voters-remaining')
);
const VoterTurnoutModeling = lazy(() => import('src/sections/planning/voter-turnout-modeling'));
const IndividualAnalysis = lazy(() => import('src/sections/planning/individual-analysis'));
const ElectionHistory = lazy(() => import('src/sections/planning/election-history'));
const VoterList = lazy(() => import('src/sections/planning/voter-list'));
const AbsenteeTracker = lazy(() => import('src/sections/planning/absentee-tracker'));

export const superadminRoutes = [
  {
    path: 'superadmin',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: 'organizations',
        children: [
          { index: true, element: <Organizations /> },
          {
            path: 'create',
            element: <OrganizationsCreate />,
          },
          {
            path: ':orgId',
            element: <Outlet />,
            children: [
              { path: 'edit', element: <OrganizationsEdit /> },
              { path: 'users', element: <OrgUsers /> },
              { path: 'users/create', element: <UserCreatePage /> },
              { path: 'users/:userId/edit', element: <OrgEditUser /> },
              {
                path: 'campaigns',
                element: <Outlet />,
                children: [
                  { element: <Campaigns />, index: true },
                  { path: 'create', element: <CampaignsCreate /> },
                  { path: ':id/edit', element: <CampaignsEdit /> },
                ],
              },
              {
                path: 'tasks',
                element: <Outlet />,
                children: [
                  { element: <OrgTasks />, index: true },
                  { path: ':campaignId/create', element: <OrgTasksCreate /> },
                  { path: ':taskId/edit', element: <OrgTasksEdit /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'phone-numbers',
        children: [{ index: true, element: <AdminManagePhoneNumbersPage /> }],
      },
      {
        // superadminRoutes only wraps in AuthGuard, so this route carries its own
        // role check. The real gate is the Hasura superadmin select permission.
        path: 'status',
        element: (
          <SuperAdminGuard>
            <SystemStatus />
          </SuperAdminGuard>
        ),
      },
      {
        // Same shape as status: role check here, the real gate is Hasura — only the
        // superadmin role holds the unfiltered select + mutations on announcements.
        path: 'announcements',
        element: (
          <SuperAdminGuard>
            <Announcements />
          </SuperAdminGuard>
        ),
      },
      {
        path: 'users',
        children: [
          { index: true, element: <AdminUsers /> },
          {
            path: 'create',
            element: <AdminUsersCreate />,
          },
          {
            path: ':userId',
            element: <AdminUsersEdit />,
          },
        ],
      },
      {
        path: 'user-management',
        children: [
          { index: true, element: <Users /> },
          {
            path: 'create',
            element: <UsersCreate />,
          },
          // {
          //   path: ':userId',
          //   element: <UsersEdit />,
          // },
        ],
      },

      {
        path: 'planning',
        element: <Planning />,
      },
      {
        path: 'voter-analytics',
        children: [
          {
            path: 'geographical-summary',
            element: <GeographicalSummary />,
          },
          {
            path: 'new-voter-registration-summary',
            element: <NewVoterRegistrationSummary />,
          },
          {
            path: 'potential-voters-remaining',
            element: <PortentialVotersRemaining />,
          },
          {
            path: 'voter-turnout-modeling',
            element: <VoterTurnoutModeling />,
          },
          {
            path: 'individual-analysis',
            element: <IndividualAnalysis />,
          },
          {
            path: 'election-history',
            element: <ElectionHistory />,
          },
          {
            path: 'voter-list',
            element: <VoterList />,
          },
          {
            path: 'absentee-tracker',
            element: <AbsenteeTracker />,
          },
        ],
      },
    ],
  },
];
